/**
 * trackVisionReportNormalizer.js
 * ===============================
 * Single source of truth for normalizing TrackVision report JSON
 * into a predictable shape for UI rendering.
 *
 * Handles multiple backend versions and nested structures.
 */

const PHASE_NAMES_BY_INDEX = [
  'Saída do Bloco',
  'Aceleração',
  'Transição',
  'Velocidade Máxima',
  'Desaceleração',
];

const PHASE_LABEL_MAP = {
  block_start: 'Saída do Bloco',
  drive: 'Aceleração',
  transition: 'Transição',
  max_velocity: 'Velocidade Máxima',
  deceleration: 'Desaceleração',
  approach: 'Aproximação',
  hurdle_clearance: 'Passagem de Barreiras',
  run_in: 'Final',
  start_acceleration: 'Aceleração Inicial',
  transition_to_pace: 'Transição p/ Ritmo',
  steady_state: 'Ritmo Estável',
  kick: 'Sprint Final',
  initial_acceleration: 'Aceleração Inicial',
  final_push: 'Sprint Final',
};

const PHASE_COLORS = {
  'Saída do Bloco': '#1f2937',
  'Aceleração': '#374151',
  'Transição': '#6b7280',
  'Velocidade Máxima': '#000000',
  'Desaceleração': '#9ca3af',
  'Aproximação': '#374151',
  'Passagem de Barreiras': '#000000',
  'Final': '#6b7280',
  'Aceleração Inicial': '#374151',
  'Transição p/ Ritmo': '#6b7280',
  'Ritmo Estável': '#000000',
  'Sprint Final': '#1f2937',
};

/**
 * Extract the actual report object from various response shapes.
 * Handles: raw report, { report_json }, { report }, { data: { report } }, string JSON, etc.
 */
function extractReportObject(input) {
  if (!input) return null;

  // If it's a string, try to parse it
  if (typeof input === 'string') {
    try {
      return extractReportObject(JSON.parse(input));
    } catch {
      return null;
    }
  }

  // Already a parsed report with known fields
  if (input.cadence_spm || input.phases || input.movement_quality_score) {
    return input;
  }

  // Nested in report_json (string or object)
  if (input.report_json) {
    const rj = typeof input.report_json === 'string'
      ? JSON.parse(input.report_json)
      : input.report_json;
    return rj;
  }

  // Nested in .report
  if (input.report && typeof input.report === 'object') {
    return extractReportObject(input.report);
  }

  // Nested in .data
  if (input.data && typeof input.data === 'object') {
    return extractReportObject(input.data);
  }

  // Nested in .metrics (some versions)
  if (input.metrics && (input.metrics.cadence_spm || input.metrics.ground_contact_ms)) {
    return input.metrics;
  }

  // Nested in .summary
  if (input.summary && (input.summary.cadence_spm || input.summary.cadence)) {
    return input.summary;
  }

  return input;
}

/**
 * Normalize a TrackVision report into a predictable flat structure.
 * @param {any} rawReport - The report in any shape from the backend
 * @param {object} session - Optional session object for fallback values
 * @returns {object|null} Normalized report or null if no data
 */
export function normalizeTrackVisionReport(rawReport, session = {}) {
  const r = extractReportObject(rawReport);
  if (!r) return null;

  // Metrics can be at root or nested
  const metrics = r.metrics || r;
  const summary = r.summary || r.executive_summary || {};
  const sym = r.symmetry || {};

  // ─── Core Metrics (with multi-format fallbacks) ───
  const cadenceSpm =
    r.cadence_spm ?? metrics.cadence_spm ?? metrics.cadence ?? summary.cadence_spm ?? summary.cadence ?? session.cadence_spm ?? null;

  const totalStrides =
    r.total_strides ?? metrics.total_strides ?? metrics.total_steps ?? metrics.stride_count ?? session.total_strides ?? null;

  const groundContactMs =
    r.ground_contact_ms ?? metrics.ground_contact_ms ?? metrics.contact_time_ms ?? metrics.contact_ms ?? null;

  const flightTimeMs =
    r.flight_time_ms ?? metrics.flight_time_ms ?? metrics.flight_ms ?? metrics.air_time_ms ?? null;

  const asymmetryPct =
    r.asymmetry_score ?? sym.overall_asymmetry_pct ?? sym.asymmetry_index ?? metrics.asymmetry_pct ?? r.asymmetry_pct ?? session.asymmetry_score ?? null;

  const velocityPeakMs =
    r.velocity_peak_ms ?? metrics.velocity_peak_ms ?? metrics.peak_velocity ?? null;

  const velocityDecrementPct =
    r.velocity_decrement_pct ?? metrics.velocity_decrement_pct ?? metrics.velocity_loss_pct ?? null;

  const strideLength =
    r.stride_length ?? metrics.stride_length ?? metrics.avg_stride_length_m ?? null;

  const movementQualityScore =
    r.movement_quality_score ?? metrics.movement_quality_score ?? session.movement_quality_score ?? null;

  const dutyFactor = r.duty_factor ?? metrics.duty_factor ?? null;
  const shinAngleDeg = r.shin_angle_touchdown_deg ?? metrics.shin_angle_touchdown_deg ?? null;
  const kneeAtTouchdownDeg = r.knee_at_touchdown_deg ?? metrics.knee_at_touchdown_deg ?? null;
  const verticalOscillationCm = r.vertical_oscillation_cm ?? metrics.vertical_oscillation_cm ?? null;
  const firstStepLengthM = r.first_step_length_m ?? metrics.first_step_length_m ?? null;

  // ─── Phases ───
  const rawPhases = r.phases || r.sprint_phases || r.phase_segments || session.phases || [];
  const phases = rawPhases.map((p, i) => {
    const phaseName = p.phase_name || p.phase || p.name || null;
    const label = phaseName
      ? (PHASE_LABEL_MAP[phaseName] || phaseName)
      : (PHASE_NAMES_BY_INDEX[i] || `Fase ${i + 1}`);

    return {
      name: label,
      rawName: phaseName,
      duration: p.duration_s ?? p.duration ?? null,
      startFrame: p.start_frame ?? p.startFrame ?? null,
      endFrame: p.end_frame ?? p.endFrame ?? null,
      confidence: p.confidence ?? null,
      color: PHASE_COLORS[label] || '#6b7280',
      metrics: p.metrics || {},
    };
  });

  // Compute cumulative times for timeline
  let cumTime = 0;
  const phasesWithTime = phases.map(p => {
    const startTime = cumTime;
    const endTime = p.duration != null ? cumTime + p.duration : null;
    if (p.duration != null) cumTime += p.duration;
    return { ...p, startTime, endTime };
  });

  // ─── Angles ───
  const rawAngles = r.angles || session.angles || [];
  const angles = rawAngles.map(a => ({
    joint: a.joint || a.joint_name || 'unknown',
    context: a.context || null,
    mean: a.mean ?? a.mean_deg ?? null,
    std: a.std ?? a.std_deg ?? null,
    min: a.min ?? a.min_deg ?? null,
    max: a.max ?? a.max_deg ?? null,
    optimalMin: a.optimal_min ?? null,
    optimalMax: a.optimal_max ?? null,
    inRangePct: a.in_range_pct ?? null,
    confidence: a.confidence ?? null,
  }));

  // ─── Symmetry ───
  const symmetry = {
    asymmetryIndex: sym.asymmetry_index ?? asymmetryPct,
    gctAsymmetryPct: sym.gct_asymmetry_pct ?? null,
    strideLengthAsymmetryPct: sym.stride_length_asymmetry_pct ?? null,
    kneeAngleDiffDeg: sym.knee_angle_diff_deg ?? null,
    hipExtensionDiffDeg: sym.hip_extension_diff_deg ?? null,
    riskFlag: sym.risk_flag ?? false,
  };

  // ─── Video Quality ───
  const vq = r.video_quality || {};
  const videoQuality = {
    score: vq.score ?? null,
    resolution: vq.resolution ?? null,
    fpsDetected: vq.fps_detected ?? r.video_fps ?? null,
    cameraType: vq.camera_type ?? null,
    warnings: vq.warnings || [],
  };

  // ─── Executive Summary ───
  const exec = r.executive_summary || summary || {};
  const executiveSummary = {
    headline: exec.headline ?? null,
    findings: exec.findings || [],
    topStrength: exec.top_strength ?? null,
    topImprovement: exec.top_improvement ?? null,
    recommendation: exec.recommendation ?? null,
  };

  // ─── MQS Breakdown ───
  const mqsRaw = r.mqs_breakdown || {};
  const mqsBreakdown = {
    total: mqsRaw.total ?? movementQualityScore,
    components: mqsRaw.components || null,
    benchmarkLevel: mqsRaw.benchmark_level ?? null,
  };

  // ─── Velocity Curve ───
  const velocityCurve = (r.velocity_curve || []).map(pt => ({
    distance: pt.distance_m ?? pt.distance ?? 0,
    velocity: pt.velocity_ms ?? pt.velocity ?? 0,
  }));

  // ─── Confidence Map ───
  const confidenceMap = r.confidence_map || {};

  // ─── Meta ───
  const meta = {
    pipelineVersion: r.pipeline_version ?? null,
    modelVersion: r.model_version ?? null,
    benchmarkVersion: r.benchmark_version ?? null,
    generatedAt: r.generated_at ?? null,
    eventType: r.event_type ?? session.event_type ?? null,
    analysisMode: r.analysis_mode ?? null,
  };

  // Check if we got ANY meaningful data
  const hasData = cadenceSpm != null || groundContactMs != null ||
    velocityPeakMs != null || phases.length > 0 || movementQualityScore != null;

  if (!hasData) return null;

  return {
    // Core metrics
    cadenceSpm,
    totalStrides,
    groundContactMs,
    flightTimeMs,
    asymmetryPct,
    velocityPeakMs,
    velocityDecrementPct,
    strideLength,
    movementQualityScore,
    dutyFactor,
    shinAngleDeg,
    kneeAtTouchdownDeg,
    verticalOscillationCm,
    firstStepLengthM,

    // Structured data
    phases: phasesWithTime,
    angles,
    symmetry,
    videoQuality,
    executiveSummary,
    mqsBreakdown,
    velocityCurve,
    confidenceMap,
    meta,

    // Raw access (for backwards compat)
    _raw: r,
  };
}

export default normalizeTrackVisionReport;
